/**
 * 明阳智能 - 知识库（检查项）管理接口（task_name: mingyang_knowledge）
 * 协议来源：Web管理后台-知识库管理.md
 */
import { callMingyangTask } from './client'
import { ExportTaskBrief, ExportTaskDetail } from './project'

const TASK = 'mingyang_knowledge' as const

// ------------------------------------------------------------
// 类型
// ------------------------------------------------------------

export type KnowledgeStatus = 0 | 1

export interface MediaNum {
  photo: number
  video: number
}

export interface RuleDetail {
  rule_detail_id?: number // 新增项不带，更新/删除带
  obj: string
  spec: string
  uncertainty: string
  range: string
  coverage: string
  rule: string
}

// ------------------------------------------------------------
// 4.1 检查项统计
// ------------------------------------------------------------

export interface KnowledgeStatsResponse {
  total_knowledges: number
  active_knowledges: number
  disabled_knowledges: number
  total_machine_models: number
}

export function fetchKnowledgeStats(dispatch: (...args: any[]) => any) {
  return callMingyangTask<KnowledgeStatsResponse>(dispatch, TASK, 'stats', {})
}

// ------------------------------------------------------------
// 4.2 检查项列表
// ------------------------------------------------------------

export interface KnowledgeListParams {
  inspection?: string
  machine_model?: string
  start_time?: string
  end_time?: string
  status?: KnowledgeStatus | null
  page?: number
  page_size?: number
}

export interface KnowledgeListItem {
  knowledge_id: number
  inspection: string
  machine_model: string[]
  media_num: MediaNum
  rule_count: number
  watermark_rule: string
  operator_name: string
  update_time: string
  status: KnowledgeStatus
}

export interface KnowledgeListResponse {
  total: number
  page: number
  page_size: number
  list: KnowledgeListItem[]
}

export async function fetchKnowledgeList(
  dispatch: (...args: any[]) => any,
  params: KnowledgeListParams = {}
): Promise<KnowledgeListResponse> {
  const data = await callMingyangTask<KnowledgeListResponse>(
    dispatch,
    TASK,
    'list',
    {
      inspection: params.inspection || '',
      machine_model: params.machine_model || '',
      start_time: params.start_time || '',
      end_time: params.end_time || '',
      status: params.status ?? null,
      page: params.page || 1,
      page_size: params.page_size || 20
    }
  )
  // 字段兜底：
  //  - id ↔ knowledge_id
  //  - last_operator_name ↔ operator_name
  //  - rule_count 接口未返回时默认 0
  return {
    ...data,
    list: ((data && data.list) || []).map((r: any) => ({
      ...r,
      knowledge_id: r.knowledge_id ?? r.id,
      operator_name: r.operator_name ?? r.last_operator_name ?? '',
      rule_count: r.rule_count ?? 0
    }))
  }
}

// ------------------------------------------------------------
// 4.3 检查项详情
// ------------------------------------------------------------

export interface KnowledgeDetailResponse {
  knowledge_id: number
  inspection: string
  machine_model: string[]
  media_num: MediaNum
  watermark_rule: string
  /** 最长等待时间（秒），从首张照片到强制触发 AI 分析的最长时间。默认 300 */
  max_wait: number
  bailian_id: string
  status: KnowledgeStatus
  create_time: string
  update_time: string
  rule_details: Array<Required<Pick<RuleDetail, 'rule_detail_id'>> & RuleDetail>
}

export async function fetchKnowledgeDetail(
  dispatch: (...args: any[]) => any,
  knowledge_id: number
): Promise<KnowledgeDetailResponse> {
  // 后端入参字段是 id（不是 knowledge_id），同时兜底两种写法兼容
  const d = await callMingyangTask<any>(dispatch, TASK, 'detail', {
    id: knowledge_id,
    knowledge_id
  })
  // 后端 detail 字段兜底：
  //  - id ↔ knowledge_id
  //  - last_operator_name ↔ operator_name
  //  - watermark_rule 可能是 null，归一为空字符串避免渲染 null
  //  - machine_model 可能未返回，默认 []
  //  - rule_details[].id ↔ rule_detail_id
  return {
    ...d,
    knowledge_id: d?.knowledge_id ?? d?.id,
    operator_name: d?.operator_name ?? d?.last_operator_name ?? '',
    watermark_rule: d?.watermark_rule ?? '',
    machine_model: Array.isArray(d?.machine_model) ? d.machine_model : [],
    rule_details: ((d && d.rule_details) || []).map((r: any) => ({
      ...r,
      rule_detail_id: r.rule_detail_id ?? r.id
    }))
  }
}

// ------------------------------------------------------------
// 4.4 创建检查项
// ------------------------------------------------------------

export interface CreateKnowledgeParams {
  inspection: string
  machine_model: string[]
  media_num: MediaNum
  watermark_rule?: string
  /** 最长等待时间（秒），不传则后端默认 300 */
  max_wait?: number
  bailian_id?: string
  rule_details?: RuleDetail[]
  group_id: string
  user_id: string
}

export function createKnowledge(
  dispatch: (...args: any[]) => any,
  params: CreateKnowledgeParams
) {
  const body: Record<string, any> = {
    inspection: params.inspection,
    machine_model: params.machine_model,
    media_num: params.media_num,
    watermark_rule: params.watermark_rule || '',
    bailian_id: params.bailian_id || '',
    rule_details: params.rule_details || [],
    group_id: params.group_id,
    user_id: params.user_id
  }
  if (params.max_wait !== undefined) body.max_wait = params.max_wait
  return callMingyangTask<{ knowledge_id: number }>(
    dispatch,
    TASK,
    'create',
    body
  )
}

// ------------------------------------------------------------
// 4.5 更新检查项（rule_details 全量替换）
// ------------------------------------------------------------

export interface UpdateKnowledgeParams {
  knowledge_id: number
  inspection?: string
  machine_model?: string[]
  media_num?: MediaNum
  watermark_rule?: string
  /** 最长等待时间（秒） */
  max_wait?: number
  bailian_id?: string
  rule_details?: RuleDetail[]
  group_id: string
  user_id: string
}

export function updateKnowledge(
  dispatch: (...args: any[]) => any,
  params: UpdateKnowledgeParams
) {
  // 仅传入存在的字段，避免覆盖；后端入参字段是 id，同时兼容 knowledge_id
  const body: Record<string, any> = {
    id: params.knowledge_id,
    knowledge_id: params.knowledge_id,
    group_id: params.group_id,
    user_id: params.user_id
  }
  if (params.inspection !== undefined) body.inspection = params.inspection
  if (params.machine_model !== undefined)
    body.machine_model = params.machine_model
  if (params.media_num !== undefined) body.media_num = params.media_num
  if (params.watermark_rule !== undefined)
    body.watermark_rule = params.watermark_rule
  if (params.max_wait !== undefined) body.max_wait = params.max_wait
  if (params.bailian_id !== undefined) body.bailian_id = params.bailian_id
  if (params.rule_details !== undefined) body.rule_details = params.rule_details

  return callMingyangTask<{ success: boolean }>(dispatch, TASK, 'update', body)
}

// ------------------------------------------------------------
// 4.6 停用/启用检查项
// ------------------------------------------------------------

export interface ToggleKnowledgeStatusParams {
  knowledge_id: number
  status: KnowledgeStatus
  group_id: string
  user_id: string
}

export function toggleKnowledgeStatus(
  dispatch: (...args: any[]) => any,
  params: ToggleKnowledgeStatusParams
) {
  return callMingyangTask<{
    success: boolean
    status_before: KnowledgeStatus
    status_after: KnowledgeStatus
  }>(dispatch, TASK, 'toggle_status', {
    id: params.knowledge_id,
    knowledge_id: params.knowledge_id,
    status: params.status,
    group_id: params.group_id,
    user_id: params.user_id
  })
}

// ------------------------------------------------------------
// 4.7 发起导出
// ------------------------------------------------------------

export interface KnowledgeExportParams {
  inspection?: string
  machine_model?: string
  start_time?: string
  end_time?: string
  status?: KnowledgeStatus | null
  group_id: string
  user_id: string
}

export function startKnowledgeExport(
  dispatch: (...args: any[]) => any,
  params: KnowledgeExportParams
) {
  return callMingyangTask<ExportTaskBrief>(dispatch, TASK, 'export', {
    inspection: params.inspection || '',
    machine_model: params.machine_model || '',
    start_time: params.start_time || '',
    end_time: params.end_time || '',
    status: params.status ?? null,
    group_id: params.group_id,
    user_id: params.user_id
  })
}

// ------------------------------------------------------------
// 4.7.1 导出状态
// ------------------------------------------------------------

export function fetchKnowledgeExportStatus(
  dispatch: (...args: any[]) => any,
  export_id: number
) {
  return callMingyangTask<ExportTaskDetail>(dispatch, TASK, 'export_status', {
    id: export_id,
    export_id
  })
}

// ------------------------------------------------------------
// 4.7.2 导出历史
// ------------------------------------------------------------

export interface KnowledgeExportListResponse {
  total: number
  list: ExportTaskDetail[]
}

export function fetchKnowledgeExportList(
  dispatch: (...args: any[]) => any,
  params: { user_id: string; page?: number; page_size?: number }
) {
  return callMingyangTask<KnowledgeExportListResponse>(
    dispatch,
    TASK,
    'export_list',
    {
      user_id: params.user_id,
      page: params.page || 1,
      page_size: params.page_size || 10
    }
  )
}

// ------------------------------------------------------------
// 4.8 筛选下拉选项
// ------------------------------------------------------------

export interface KnowledgeFilterOptionsResponse {
  machine_model_options: string[]
  status_options: { value: number; label: string }[]
}

export function fetchKnowledgeFilterOptions(dispatch: (...args: any[]) => any) {
  return callMingyangTask<KnowledgeFilterOptionsResponse>(
    dispatch,
    TASK,
    'filter_options',
    {}
  )
}

// ------------------------------------------------------------
// 检查项选项（用于项目管理弹窗的多选）— 检查项配置文档 4.8
// ------------------------------------------------------------

export interface KnowledgeOption {
  knowledge_id: number
  inspection: string
}

export interface KnowledgeOptionsResponse {
  list: KnowledgeOption[]
}

export function fetchKnowledgeOptions(
  dispatch: (...args: any[]) => any,
  params: { keyword?: string; status?: KnowledgeStatus } = {}
) {
  return callMingyangTask<KnowledgeOptionsResponse>(dispatch, TASK, 'options', {
    keyword: params.keyword || '',
    status: params.status ?? 1
  })
}
